import React from "react";
import { ProgramTemplateCategory } from "@growth-machine-llc/stridist-api-client";
import { ExerciseCategoriesFilter } from "../../exercise-library/filters/ExerciseCategoriesFilters";
import { mapFilters } from "../../exercise-library/filters/filterUtils";

const getCategoryLabel = (category: ProgramTemplateCategory): string => {
  if (category === ProgramTemplateCategory.NONE) return "None";
  return category
    .split("_")
    .map((word) => word.charAt(0) + word.slice(1).toLowerCase())
    .join(" ");
};

const ProgramTemplateCategoryLabels = Object.values(
  ProgramTemplateCategory,
).reduce(
  (acc, category) => {
    acc[category] = getCategoryLabel(category);
    return acc;
  },
  {} as Record<ProgramTemplateCategory, string>,
);

export const CategoryFilter = React.memo(function CategoryFilter({
  onChange,
  contextFilters,
  className,
}: {
  onChange?: (selected: ProgramTemplateCategory[]) => void;
  contextFilters?: ProgramTemplateCategory[];
  className?: string;
}) {
  const [filters, setFilters] = React.useState(() =>
    mapFilters(ProgramTemplateCategory, contextFilters),
  );

  React.useEffect(() => {
    setFilters(mapFilters(ProgramTemplateCategory, contextFilters));
  }, [contextFilters]);

  const extractSelected = React.useCallback(
    (
      filters: Record<ProgramTemplateCategory, boolean>,
    ): ProgramTemplateCategory[] =>
      Object.entries(filters)
        .filter(([_, checked]) => checked)
        .map(([key]) => key as ProgramTemplateCategory),
    [],
  );

  const handleChange = React.useCallback(
    (newFilters: Record<ProgramTemplateCategory, boolean>) => {
      const selected = extractSelected(newFilters);
      onChange?.(selected);
    },
    [extractSelected, onChange],
  );

  return (
    <ExerciseCategoriesFilter
      filters={filters}
      onChange={handleChange}
      filterTypesLabels={ProgramTemplateCategoryLabels}
      labelType="Type"
      className={className}
    />
  );
});
